// Overhead — the Overhead tab of Finance › P&L. Monthly entries for costs no
// other table holds; the P&L reads them by month.
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { format, parse } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';

// No "Admin Payroll": salaried office staff are paid through payroll runs
// (PayrollRunDetail auto-includes them), and the P&L adds payroll and
// overhead together — a category for it here counted them twice. Existing
// rows keep whatever category they were saved with.
const CATEGORY_OPTIONS = [
  'Rent', 'Utilities', 'Insurance', 'Supplies',
  'Software & Licensing', 'Professional Fees', 'Marketing', 'Other',
];

const emptyForm = () => ({
  month: format(new Date(), 'yyyy-MM'),
  category: CATEGORY_OPTIONS[0],
  description: '',
  amount: '',
});

const fmt$ = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OverheadExpenses() {
  const { user, profile } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('overhead_expenses')
      .select('*')
      .order('month', { ascending: false })
      .order('created_at', { ascending: false });
    setEntries(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setError(''); setShowModal(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ month: e.month.slice(0, 7), category: e.category, description: e.description || '', amount: String(e.amount) });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.month || !form.category || !form.amount) { setError('Month, category, and amount are required.'); return; }
    setSaving(true);
    setError('');
    const monthDate = format(parse(form.month, 'yyyy-MM', new Date()), 'yyyy-MM-01');
    const payload = {
      month: monthDate,
      category: form.category,
      description: form.description || null,
      amount: Number(form.amount),
    };
    const { error: err } = editing
      ? await supabase.from('overhead_expenses').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', editing.id)
      : await supabase.from('overhead_expenses').insert([{
          ...payload,
          created_by: user?.id || null,
          created_by_name: profile?.full_name || user?.email || 'unknown',
        }]);
    setSaving(false);
    if (err) { setError(err.message); return; }
    setShowModal(false);
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error: err } = await supabase.from('overhead_expenses').delete().eq('id', deleteId);
    setDeleteId(null);
    if (err) { setError(err.message); return; }
    load();
  };

  // Group by month for display
  const groups = {};
  entries.forEach(e => {
    const key = e.month.slice(0, 7);
    (groups[key] = groups[key] || []).push(e);
  });
  const monthKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 520 }}>
          Costs no other table holds — rent, insurance, supplies, software. Office salaries are not entered here;
          they are paid through payroll runs. Each month's total feeds the P&amp;L tab, except in months QuickBooks
          has synced, where overhead comes from the books instead.
        </div>
        <button onClick={openAdd} className="btn btn-primary" style={{ flexShrink: 0 }}>+ Add expense</button>
      </div>

      {error && !showModal && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>⚠ {error}</div>
      )}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
      ) : monthKeys.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
          No overhead expenses logged yet.
        </div>
      ) : monthKeys.map(key => {
        const rows = groups[key];
        const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
        return (
          <div key={key} className="card" style={{ marginBottom: 14 }}>
            <div className="card-header">
              <div className="card-title">{format(parse(key, 'yyyy-MM', new Date()), 'MMMM yyyy')}</div>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{fmt$(total)}</span>
            </div>
            {rows.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                borderBottom: '0.5px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{r.category}</div>
                  {r.description && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{r.description}</div>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', minWidth: 90, textAlign: 'right' }}>{fmt$(r.amount)}</div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openEdit(r)} className="btn" style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                  <button onClick={() => setDeleteId(r.id)} className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* Add/edit modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{editing ? 'Edit expense' : 'Add expense'}</div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {error && (
                <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7,
                  padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Month</div>
                  <input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                      background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Category</div>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                      background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                    {[...CATEGORY_OPTIONS, ...(form.category && !CATEGORY_OPTIONS.includes(form.category) ? [form.category] : [])].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Description (optional)</div>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                    background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Amount</div>
                <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
                    background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowModal(false)} className="btn">Cancel</button>
                <button onClick={handleSave} className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 360, padding: 20,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Delete this expense?</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>This can't be undone.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDeleteId(null)} className="btn">Cancel</button>
              <button onClick={handleDelete} className="btn" style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
